'use client';

import { createContext, useContext, type ReactNode } from 'react';
import type { JoeConnectionActions } from './useJoeConnection';

/**
 * Inert defaults so a component rendered outside the provider - a story, a test, a future host -
 * stays harmless instead of throwing: the actions do nothing and every button reads as unavailable.
 */
const inertActions: JoeConnectionActions = {
  connect: () => {},
  canConnect: false,
  isConnecting: false,
  disconnect: () => {},
  isDisconnecting: false,
};

const JoeConnectionContext = createContext<JoeConnectionActions>(inertActions);

/**
 * Carries the jö actions to both consumers.
 *
 * The connected section and the disconnected card sit in different subtrees - one is embedded in the
 * personal customer card, the other is a standalone grid item - so a context keeps `CustomerCard`
 * free of loyalty props it has no use for. Mirrors the legacy `JoeProvider` for that same reason.
 *
 * The value is owned by the caller (`CustomerOverview` via `useJoeConnection`), which also needs the
 * presentation state for placement; that keeps a single hook instance and a single set of requests.
 */
export const JoeConnectionProvider = ({
  value,
  children,
}: {
  value: JoeConnectionActions;
  children: ReactNode;
}) => <JoeConnectionContext.Provider value={value}>{children}</JoeConnectionContext.Provider>;

export const useJoeConnectionActions = () => useContext(JoeConnectionContext);
